import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import implementationChecklistService from '../services/implementationChecklistService';
import toast from 'react-hot-toast';
import i18n from '../i18n';

const normalize = (str) => (str || '').toLowerCase().trim();

const UNASSIGNED_VALUES = ['Non affecté', 'Not assigned', 'Unassigned'];

const resolveResponsible = (raw, notAssignedValue) => {
  if (!raw || UNASSIGNED_VALUES.includes(raw)) return notAssignedValue;
  return raw;
};

const useImplementationChecklistStore = create(
  subscribeWithSelector((set, get) => ({
    checklistData: null,
    checklistQuestions: [],
    checklistHistory: [],
    checklistGenerated: false,
    approvalStatus: null,
    versionInfo: {
      currentVersion: 1,
      isLocked: false,
      status: 'draft',
      allVersions: [],
      rejectionReason: null
    },
    isLoading: false,
    isGenerating: false,
    isSaving: false,
    generationProgress: 0,
    error: null,
    analysisId: null,

    resetState: () => {
      set({
        checklistData: null,
        checklistQuestions: [],
        checklistHistory: [],
        checklistGenerated: false,
        approvalStatus: null,
        versionInfo: {
          currentVersion: 1,
          isLocked: false,
          status: 'draft',
          allVersions: [],
          rejectionReason: null
        },
        isLoading: false,
        isGenerating: false,
        isSaving: false,
        generationProgress: 0,
        error: null,
        analysisId: null
      });
    },

    loadGlobalChecklist: async (analysisId) => {
      set({ isLoading: true, error: null, analysisId });
      try {
        const result = await implementationChecklistService.getGlobalUnified(analysisId);
        if (!result.success) {
          set({ isLoading: false, error: result.error || 'Erreur de chargement' });
          return { success: false, error: result.error };
        }
        set({ checklistData: result.data, isLoading: false });
        return { success: true, data: result.data };
      } catch (error) {
        console.error('[useImplementationChecklistStore] loadGlobalChecklist error:', error);
        set({ isLoading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    generateChecklist: async ({ analysisId, language = 'fr' } = {}) => {
      const { checklistData } = get();
      const targetId = analysisId || get().analysisId;
      if (!targetId) {
        toast.error(i18n.t('common.error') || 'Erreur');
        return { success: false };
      }

      set({ isGenerating: true, generationProgress: 0, error: null });

      const allMeasures = checklistData?.measures || [];
      const scenariosCount = checklistData?.scenarios?.length || 0;

      const progressInterval = setInterval(() => {
        set((state) => {
          if (state.generationProgress >= 100) return state;
          return { generationProgress: state.generationProgress + 1 };
        });
      }, 100);

      try {
        const result = await implementationChecklistService.generateQuestions(targetId, {
          globalGeneration: true,
          allMeasures,
          scenariosCount,
          language
        });

        clearInterval(progressInterval);

        if (!result.success || !result.data?.data?.questions) {
          throw new Error(result.error || 'Generation failed');
        }

        const newQuestions = result.data.data.questions;

        set((state) => {
          if (!state.checklistData) return state;
          const updatedResponses = [...(state.checklistData.checklistResponses || [])];
          const globalIndex = updatedResponses.findIndex((r) => r.scenarioId === 'global');
          const existingHistory =
            globalIndex >= 0 ? updatedResponses[globalIndex].checklistData?.history || [] : [];

          const newResponse = {
            scenarioId: 'global',
            checklistData: {
              currentQuestions: newQuestions,
              history: existingHistory
            }
          };

          if (globalIndex >= 0) {
            updatedResponses[globalIndex] = { ...updatedResponses[globalIndex], ...newResponse };
          } else {
            updatedResponses.push(newResponse);
          }

          return {
            checklistQuestions: newQuestions,
            checklistGenerated: true,
            checklistHistory: existingHistory,
            checklistData: {
              ...state.checklistData,
              checklistResponses: updatedResponses,
              aiChecklistResponses: { questions: newQuestions }
            },
            generationProgress: 100
          };
        });

        await implementationChecklistService.saveResponsesGlobal(targetId, {
          responses: newQuestions,
          questions: newQuestions,
          updateCurrentVersion: true
        });

        set({ isGenerating: false });
        return { success: true, questions: newQuestions };
      } catch (error) {
        clearInterval(progressInterval);
        console.error('[useImplementationChecklistStore] generateChecklist error:', error);
        set({ isGenerating: false, error: error.message });
        toast.error(i18n.t('common.error') || 'Erreur lors de la génération');
        return { success: false, error: error.message };
      }
    },

    deriveQuestions: (selectedResponsible) => {
      const { checklistData, checklistQuestions: currentQs } = get();
      if (!checklistData) return;

      const notAssignedValue = i18n.t('implementationChecklist.notAssigned');

      let baseQuestions = [];
      let history = [];

      if (checklistData.checklistResponses) {
        const globalResponse = checklistData.checklistResponses.find((r) => r.scenarioId === 'global');
        if (globalResponse && globalResponse.checklistData) {
          const data = globalResponse.checklistData;
          if (data.currentQuestions) {
            baseQuestions = data.currentQuestions;
          } else if (data.questions) {
            baseQuestions = data.questions;
          }
          if (data.history) history = data.history;
        }
      }

      if (baseQuestions.length === 0 && checklistData.aiChecklistResponses?.questions) {
        baseQuestions = checklistData.aiChecklistResponses.questions;
      }

      const currentResponsesMap = new Map();
      currentQs.forEach((q) => {
        if (q.response !== null && q.response !== undefined) {
          currentResponsesMap.set(q.id, { response: q.response, justification: q.justification || '' });
        }
      });

      if (selectedResponsible) {
        const targetResp = normalize(selectedResponsible.name);
        const responsibleMeasures = (checklistData.measures || [])
          .filter((m) => m.retained !== false)
          .filter((m) => normalize(resolveResponsible(m.responsiblePerson, notAssignedValue)) === targetResp);

        const responsibleMeasureNames = responsibleMeasures.map((m) => m.name);
        const responsibleMeasureIds = responsibleMeasures.map((m) => m.id);

        let relevantQuestions = baseQuestions.filter((q) => {
          if (q.concerned_measure_ids?.length > 0) {
            return q.concerned_measure_ids.some((id) => responsibleMeasureIds.includes(id));
          }
          return q.concerned_measures?.some((mName) =>
            responsibleMeasureNames.some((rmName) => {
              const nRm = normalize(rmName);
              const nM = normalize(mName);
              return nRm === nM || nRm.includes(nM) || nM.includes(nRm);
            })
          );
        });

        const entry = checklistData.responsibleChecklists?.find(
          (r) => normalize(r.responsibleName) === targetResp
        );
        const currentVersionData =
          entry?.currentVersionData ||
          entry?.versions?.find((v) => v.versionNumber === (entry?.currentVersion || 1));

        if (currentVersionData?.responses?.length > 0) {
          relevantQuestions = relevantQuestions.map((q) => {
            const saved = currentVersionData.responses.find((s) => s.questionId === q.id);
            const current = currentResponsesMap.get(q.id);
            if (current) return { ...q, response: current.response, justification: current.justification };
            if (saved) return { ...q, response: saved.response, justification: saved.justification || '' };
            return { ...q, response: null, justification: '' };
          });
        } else {
          relevantQuestions = relevantQuestions.map((q) => {
            const current = currentResponsesMap.get(q.id);
            if (current) return { ...q, response: current.response, justification: current.justification };
            return { ...q, response: null, justification: '' };
          });
        }

        relevantQuestions = relevantQuestions.map((q) => {
          if (q.responsiblePerson) return q;
          const linkedMeasures = (q.concerned_measure_ids || [])
            .map((id) => responsibleMeasures.find((m) => m.id === id))
            .filter(Boolean);
          const responsables = [
            ...new Set(
              linkedMeasures.map((m) => resolveResponsible(m?.responsiblePerson, notAssignedValue))
            )
          ];
          return { ...q, _derivedResponsible: responsables.join(', ') || notAssignedValue };
        });

        const versionStatus = currentVersionData?.status || 'draft';
        set({
          checklistQuestions: relevantQuestions,
          checklistHistory: history,
          checklistGenerated: baseQuestions.length > 0,
          approvalStatus: versionStatus,
          versionInfo: {
            currentVersion: entry?.currentVersion || 1,
            isLocked: currentVersionData?.isLocked || false,
            status: versionStatus,
            allVersions: entry?.versions || [],
            rejectionReason: currentVersionData?.rejectionReason || null
          }
        });
      } else {
        let globalQuestionsWithResponses = [...baseQuestions];

        if (checklistData.responsibleChecklists?.length > 0) {
          const allResponses = new Map();
          checklistData.responsibleChecklists.forEach((rc) => {
            const responses = rc.currentVersionData?.responses || [];
            responses.forEach((resp) => {
              allResponses.set(resp.questionId, {
                response: resp.response,
                justification: resp.justification || '',
                responsibleName: rc.responsibleName
              });
            });
          });

          globalQuestionsWithResponses = globalQuestionsWithResponses.map((q) => {
            const current = currentResponsesMap.get(q.id);
            const saved = allResponses.get(q.id);
            if (current) return { ...q, response: current.response, justification: current.justification };
            if (saved) return { ...q, response: saved.response, justification: saved.justification };
            return q;
          });
        } else {
          globalQuestionsWithResponses = globalQuestionsWithResponses.map((q) => {
            const current = currentResponsesMap.get(q.id);
            if (current) return { ...q, response: current.response, justification: current.justification };
            return q;
          });
        }

        globalQuestionsWithResponses = globalQuestionsWithResponses.map((q) => {
          if (q.responsiblePerson) return q;
          const linkedMeasures = (q.concerned_measure_ids || [])
            .map((id) => (checklistData.measures || []).find((m) => m.id === id))
            .filter(Boolean);
          const responsables = [
            ...new Set(
              linkedMeasures.map((m) => resolveResponsible(m?.responsiblePerson, notAssignedValue))
            )
          ];
          return { ...q, _derivedResponsible: responsables.join(', ') || notAssignedValue };
        });

        set({
          checklistQuestions: globalQuestionsWithResponses,
          checklistHistory: history,
          checklistGenerated: baseQuestions.length > 0,
          approvalStatus: null,
          versionInfo: {
            currentVersion: 1,
            isLocked: false,
            status: 'draft',
            allVersions: [],
            rejectionReason: null
          }
        });
      }
    },

    setResponse: (questionId, response, selectedResponsible) => {
      const { versionInfo } = get();
      if (versionInfo.isLocked && selectedResponsible) {
        return {
          success: false,
          locked: true,
          message: `La version ${versionInfo.currentVersion} est validée et verrouillée. Une nouvelle version sera créée lors de la prochaine sauvegarde.`
        };
      }

      set((state) => {
        const updatedChecklistData = { ...state.checklistData };
        if (!updatedChecklistData) return state;

        const updatedResponses = updatedChecklistData.checklistResponses?.map((resGroup) => {
          if (resGroup.scenarioId === 'global') {
            return {
              ...resGroup,
              checklistData: {
                ...resGroup.checklistData,
                currentQuestions: resGroup.checklistData.currentQuestions.map((q) =>
                  q.id === questionId ? { ...q, response } : q
                )
              }
            };
          }
          return resGroup;
        });

        let updatedResponsibles = updatedChecklistData.responsibleChecklists || [];
        if (selectedResponsible) {
          updatedResponsibles = updatedResponsibles.map((rc) => {
            if (normalize(rc.responsibleName) === normalize(selectedResponsible.name)) {
              const existing = rc.currentVersionData?.responses || [];
              const exists = existing.find((r) => r.questionId === questionId);
              const updated = exists
                ? existing.map((r) => (r.questionId === questionId ? { ...r, response } : r))
                : [...existing, { questionId, response, justification: '' }];
              return {
                ...rc,
                currentVersionData: { ...rc.currentVersionData, responses: updated }
              };
            }
            return rc;
          });
        }

        return {
          checklistData: {
            ...updatedChecklistData,
            checklistResponses: updatedResponses,
            responsibleChecklists: updatedResponsibles
          },
          checklistQuestions: state.checklistQuestions.map((q) =>
            q.id === questionId ? { ...q, response } : q
          )
        };
      });

      return { success: true, locked: false };
    },

    setJustification: (questionId, justification) => {
      set((state) => {
        const updatedResponsibles = (state.checklistData?.responsibleChecklists || []).map((rc) => {
          const existing = rc.currentVersionData?.responses || [];
          const exists = existing.find((r) => r.questionId === questionId);
          if (!exists) return rc;
          const updated = existing.map((r) =>
            r.questionId === questionId ? { ...r, justification } : r
          );
          return { ...rc, currentVersionData: { ...rc.currentVersionData, responses: updated } };
        });

        return {
          checklistData: state.checklistData
            ? { ...state.checklistData, responsibleChecklists: updatedResponsibles }
            : state.checklistData,
          checklistQuestions: state.checklistQuestions.map((q) =>
            q.id === questionId ? { ...q, justification } : q
          )
        };
      });
    },

    saveResponses: async ({
      selectedResponsible,
      status = null,
      lock = null,
      reason = null,
      forceNewVersion = false,
      updateCurrentVersion = false,
      skipReload = false
    } = {}) => {
      const { checklistQuestions, approvalStatus: currentApproval, analysisId: storedId } = get();
      const analysisId = storedId;
      if (!analysisId) return { success: false, error: 'No analysisId' };

      set({ isSaving: true });
      try {
        let response;
        const targetStatus = status || currentApproval || 'draft';

        if (selectedResponsible) {
          if (status === 'approved' || status === 'rejected') {
            response = await implementationChecklistService.approveResponsible(analysisId, {
              responsibleName: selectedResponsible.name,
              decision: status,
              reason
            });
          } else {
            response = await implementationChecklistService.saveResponsible(analysisId, {
              responsibleName: selectedResponsible.name,
              responses: checklistQuestions,
              status: targetStatus,
              proofFile: null
            });
          }
        } else if (forceNewVersion) {
          const { currentVersionIndex } = get();
          const branchFrom = currentVersionIndex !== null ? currentVersionIndex : null;
          response = await implementationChecklistService.saveResponsesGlobal(analysisId, {
            responses: checklistQuestions,
            branchFrom,
            questions: checklistQuestions,
            updateCurrentVersion: false
          });
        } else {
          response = await implementationChecklistService.saveResponsesGlobal(analysisId, {
            responses: checklistQuestions,
            questions: checklistQuestions,
            updateCurrentVersion
          });
        }

        if (!response.success) {
          throw new Error(response.error || 'Save failed');
        }

        if (selectedResponsible && status) {
          set((state) => ({
            approvalStatus: status,
            versionInfo: {
              ...state.versionInfo,
              status,
              isLocked: lock !== null ? lock : state.versionInfo.isLocked
            }
          }));
        }

        const branchMessage = forceNewVersion ? ' (nouvelle branche)' : '';
        let successMessage = `${i18n.t('implementationChecklist.saveSuccess') || 'Enregistré avec succès'}${branchMessage}`;
        if (status) {
          const labels = { submitted: 'soumise', approved: 'approuvée', rejected: 'rejetée', draft: 'brouillon' };
          successMessage = `Checklist ${labels[status] || status} avec succès.`;
        }
        toast.success(successMessage);

        if (!skipReload) {
          await get().loadGlobalChecklist(analysisId);
        }

        return { success: true, data: response.data };
      } catch (error) {
        console.error('[useImplementationChecklistStore] saveResponses error:', error);
        toast.error(`${i18n.t('common.error') || 'Erreur'}: ${error.message}`);
        return { success: false, error: error.message };
      } finally {
        set({ isSaving: false });
      }
    },

    submitForApproval: (selectedResponsible) =>
      get().saveResponses({ selectedResponsible, status: 'submitted', lock: true }),

    approve: (selectedResponsible) =>
      get().saveResponses({ selectedResponsible, status: 'approved', lock: true }),

    reject: (selectedResponsible, reason) =>
      get().saveResponses({ selectedResponsible, status: 'rejected', lock: false, reason }),

    unlock: (selectedResponsible) => {
      const { approvalStatus } = get();
      return get().saveResponses({ selectedResponsible, status: approvalStatus, lock: false });
    },

    createGlobalSnapshot: async () => {
      const { checklistData, analysisId } = get();
      if (!analysisId || !checklistData) return { success: false };

      set({ isSaving: true });
      try {
        await get().saveResponses({ forceNewVersion: true, skipReload: true });

        const approvedResponsibles = (checklistData.responsibleChecklists || []).filter(
          (r) => r.currentVersionData?.status === 'approved'
        );

        for (const responsible of approvedResponsibles) {
          try {
            const currentResponses = responsible.currentVersionData?.responses || [];
            await implementationChecklistService.saveResponsible(analysisId, {
              responsibleName: responsible.responsibleName,
              responses: currentResponses,
              status: 'draft',
              proofFile: null
            });
          } catch (err) {
            console.error(`[GLOBAL SNAPSHOT] Bump failed for ${responsible.responsibleName}:`, err);
          }
        }

        await get().loadGlobalChecklist(analysisId);
        toast.success(
          i18n.t('implementationChecklist.snapshotSuccess', { count: approvedResponsibles.length }) ||
            `Nouvelle version globale créée. ${approvedResponsibles.length} responsables mis à jour.`
        );
        return { success: true };
      } catch (error) {
        console.error('[useImplementationChecklistStore] createGlobalSnapshot error:', error);
        toast.error(`${i18n.t('common.error') || 'Erreur'}: ${error.message}`);
        return { success: false, error: error.message };
      } finally {
        set({ isSaving: false });
      }
    },

    addQuestion: (question) => {
      const newQ = {
        id: question.id || `q-${Date.now()}`,
        question: question.question,
        category: question.category,
        concerned_measures: question.concerned_measures || [],
        concerned_measure_ids: question.concerned_measure_ids || [],
        responsiblePerson: question.responsiblePerson || null,
        response: null,
        justification: ''
      };

      set((state) => {
        const updatedResponses = (state.checklistData?.checklistResponses || []).map((resGroup) => {
          if (resGroup.scenarioId === 'global') {
            return {
              ...resGroup,
              checklistData: {
                ...resGroup.checklistData,
                currentQuestions: [...(resGroup.checklistData.currentQuestions || []), newQ]
              }
            };
          }
          return resGroup;
        });
        return {
          checklistQuestions: [...state.checklistQuestions, newQ],
          checklistData: state.checklistData
            ? { ...state.checklistData, checklistResponses: updatedResponses }
            : state.checklistData
        };
      });

      return newQ;
    },

    editQuestion: (questionId, updates) => {
      set((state) => {
        const updatedResponses = (state.checklistData?.checklistResponses || []).map((resGroup) => {
          if (resGroup.scenarioId === 'global') {
            return {
              ...resGroup,
              checklistData: {
                ...resGroup.checklistData,
                currentQuestions: resGroup.checklistData.currentQuestions.map((q) =>
                  q.id === questionId ? { ...q, ...updates } : q
                )
              }
            };
          }
          return resGroup;
        });
        return {
          checklistQuestions: state.checklistQuestions.map((q) =>
            q.id === questionId ? { ...q, ...updates } : q
          ),
          checklistData: state.checklistData
            ? { ...state.checklistData, checklistResponses: updatedResponses }
            : state.checklistData
        };
      });
    },

    deleteQuestion: (questionId) => {
      set((state) => {
        const updatedResponses = (state.checklistData?.checklistResponses || []).map((resGroup) => {
          if (resGroup.scenarioId === 'global') {
            return {
              ...resGroup,
              checklistData: {
                ...resGroup.checklistData,
                currentQuestions: resGroup.checklistData.currentQuestions.filter((q) => q.id !== questionId)
              }
            };
          }
          return resGroup;
        });
        return {
          checklistQuestions: state.checklistQuestions.filter((q) => q.id !== questionId),
          checklistData: state.checklistData
            ? { ...state.checklistData, checklistResponses: updatedResponses }
            : state.checklistData
        };
      });
    }
  }))
);

export default useImplementationChecklistStore;
